<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { GENDER_OPTIONS, PREF_OPTIONS } from '@enum/common.enum.js'
import { useLearningAuthStore } from '@stores/auth'
import { useLearningUserStore } from '@stores/user'
import learningApiUser from '@src/services/api/learning/user'
import DefineValidation from '@services/validation/DefineValidation'
import { Core as YubinBangoCore } from 'yubinbango-core2'
import dayjs from 'dayjs'

const router = useRouter()
const { setRegisterInfo } = useLearningUserStore()
const { registerInfo } = storeToRefs(useLearningUserStore())

// 利用規約同意
const isConfirmAgreement = ref(false)
if (sessionStorage.getItem('jcla_group_register_agreement') === 'true') {
  isConfirmAgreement.value = true
}
const confirmAgreement = () => {
  sessionStorage.setItem('jcla_group_register_agreement', 'true')
  isConfirmAgreement.value = true
  window.scrollTo(0, 0)
}

/*
 * 入力フォーム
 */
const { errors, meta, handleSubmit, handleReset, models } = new DefineValidation((modules) => {
  return {
    email: modules.string().required().max(100).email().label('メールアドレス'),
    identifier: modules.string().required().min(4).max(16).label('ログインID'),
    password: modules.string().required().min(8).max(50).label('ログインパスワード'),
    passwordConfirm: modules.string().required().oneOf([modules.ref('password'), null], '入力したログインパスワードと一致しません。'),
    lastName: modules.string().required().min(1).max(100).label('氏名（姓）'),
    firstName: modules.string().required().min(1).max(100).label('氏名（名）'),
    lastNameKatakana: modules.string().required().min(1).max(100).katakana().fillWidth().label('フリガナ（セイ）'),
    firstNameKatakana: modules.string().required().min(1).max(100).katakana().fillWidth().label('フリガナ（メイ）'),
    nickname: modules.string().label('ニックネーム'),
    year: modules.string().required().number().birthYear().label('生年月日（年）'),
    month: modules.string().required().number().birthMonth().label('生年月日（月）'),
    date: modules.string().required().number().birthDate().label('生年月日（日）'),
    gender: modules.string().required().label('性別'),
    pref: modules.string().required().label('都道府県'),
    office: modules.string().required().max(100).label('営業所会社名'),
    officeSection: modules.string().max(100).label('営業所屋号（店舗名）'),
    officeZipcode: modules.string().required().postalCode().label('会社郵便番号'),
    officePref: modules.string().required().label('会社都道府県'),
    officeCity: modules.string().required().max(10).label('会社市区町村'),
    officeStreet: modules.string().required().max(50).label('会社番地'),
    officeBuilding: modules.string().max(50).label('会社建物名（ビル名）'),
    officeTel: modules.string().max(20).required().phoneNumber().label('会社電話番号'),
    officeFax: modules.string().label('会社FAX番号'),
    officeManagerFlag: modules.boolean().label('販売責任者フラグ')
  }
})

const birthdayError = ref('')

// 生年月日プルダウン用
const years = [...Array(100)].map((_, i) => String(dayjs().year() - i)) // 120年前まで入力可能
const months = [...Array(12)].map((_, i) => String(i + 1))
const days = [...Array(31)].map((_, i) => String(i + 1))
const intl = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', { era: 'long' })

const optsYear = computed(() => {
  return years.map((y) => {
    const jpLabel = intl.format(dayjs(`${y}-01-01`).toDate()).split('/')[0]
    return { value: `${y}`, text: `${y}(${jpLabel})` }
  })
})

watch(() => models.year.value, () => {
  // console.log('models.year.value', models.year.value)
  if (birthdayValidate(models.year.value + '/' + models.month.value + '/' + models.date.value, 'YYYY/M/D')) {
    birthdayError.value = ''
  } else {
    birthdayError.value = '生年月日が正しくありません'
  }
})

watch(() => models.month.value, () => {
  // console.log('models.month.value', models.month.value)
  if (birthdayValidate(models.year.value + '/' + models.month.value + '/' + models.date.value, 'YYYY/M/D')) {
    birthdayError.value = ''
  } else {
    birthdayError.value = '生年月日が正しくありません'
  }
})

watch(() => models.date.value, () => {
  // console.log('models.date.value', models.date.value)
  if (birthdayValidate(models.year.value + '/' + models.month.value + '/' + models.date.value, 'YYYY/M/D')) {
    birthdayError.value = ''
  } else {
    birthdayError.value = '生年月日が正しくありません'
  }
})

// 生年月日が存在する日付かチェック
const birthdayValidate = (date, format) => {
  // console.log('date', date, 'format', format)
  return dayjs(date, format).format(format) === date;
}

const moveConfirmButtonDisabled = computed(() => {
  return !(meta.value.valid && meta.value.dirty)
})

// 郵便番号のフォーマット（ハイフンなし）
const formatZipcode = (value) => {
  return value.replace(/[^\d]/g, '').slice(0, 7)
}

// 郵便番号の入力監視
watch(() => models.officeZipcode.value, (newValue) => {
  models.officeZipcode.value = formatZipcode(newValue)
})

// 郵便番号
const searchAddress = () => {
  return new YubinBangoCore(models.officeZipcode.value, (value) => {
    console.log('value', value)
    models.officePref.value = value.region // 都道府県
    models.officeCity.value = value.locality // 市区町村
    models.officeStreet.value = value.street // 町域
  })
}

const moveConfirm = handleSubmit(async (values) => {
  console.log('models', models)
  let body = {
    email: models.email.value,
    identifier: models.identifier.value,
    password: models.password.value,
    lastName: models.lastName.value,
    firstName: models.firstName.value,
    lastNameKatakana: models.lastNameKatakana.value,
    firstNameKatakana: models.firstNameKatakana.value,
    nickname: models.nickname.value,
    year: models.year.value,
    month: models.month.value,
    date: models.date.value,
    gender: models.gender.value,
    pref: models.pref.value,
    office: models.office.value,
    officeSection: models.officeSection.value,
    officeZipcode: models.officeZipcode.value,
    officePref: models.officePref.value,
    officeCity: models.officeCity.value,
    officeStreet: models.officeStreet.value,
    officeBuilding: models.officeBuilding.value,
    officeTel: models.officeTel.value,
    officeFax: models.officeFax.value,
    officeManagerFlag: models.officeManagerFlag.value
  }
  setRegisterInfo(body)
  router.push({
    name: 'RegisterConfirm'
  })
})

const isShownPassword = ref(false)
const togglePasswordSeek = () => {
  isShownPassword.value = !isShownPassword.value
}

const isShownConfirmPassword = ref(false)
const toggleConfirmPasswordSeek = () => {
  isShownConfirmPassword.value = !isShownConfirmPassword.value
}

onMounted (() => {
  window.scrollTo(0, 0)

  // 確認ページからの戻りであれば復元
  if (registerInfo.value.email) {
    models.email.value = registerInfo.value.email
    models.identifier.value = registerInfo.value.identifier
    models.password.value = registerInfo.value.password
    models.passwordConfirm.value = registerInfo.value.password
    models.lastName.value = registerInfo.value.lastName
    models.firstName.value = registerInfo.value.firstName
    models.lastNameKatakana.value = registerInfo.value.lastNameKatakana
    models.firstNameKatakana.value = registerInfo.value.firstNameKatakana
    models.nickname.value = registerInfo.value.nickname
    models.year.value = registerInfo.value.year
    models.month.value = registerInfo.value.month
    models.date.value = registerInfo.value.date
    models.gender.value = registerInfo.value.gender
    models.pref.value = registerInfo.value.pref
    models.office.value = registerInfo.value.office
    models.officeSection.value = registerInfo.value.officeSection
    models.officeZipcode.value = registerInfo.value.officeZipcode
    models.officePref.value = registerInfo.value.officePref
    models.officeCity.value = registerInfo.value.officeCity
    models.officeStreet.value = registerInfo.value.officeStreet
    models.officeBuilding.value = registerInfo.value.officeBuilding
    models.officeTel.value = registerInfo.value.officeTel
    models.officeFax.value = registerInfo.value.officeFax
    models.officeManagerFlag.value = registerInfo.value.officeManagerFlag
  } else {
    // テキスト以外は初期値を設定
    models.gender.value = '男性'
    models.officeManagerFlag.value = false
  }
})
</script>

<template>
  <section>
    <div class="container">
      <h2>新規受講登録</h2>
      <div
        v-if="!isConfirmAgreement"
        class="register_main"
      >
        <p>登録にあたり以下の規約をお読みいただき、同意の上、登録作業を行ってください。</p>
        <h3>■継続的研修受講登録規約（Web用）</h3>

        <h4>第１条（定義）</h4>
        <p>本規約における用語は、以下に定める通りとします。</p>
        <ul>
          <li>（１）「登録者」とは、本規約に同意の上、所定の方法により継続的研修受講登録をした個人をいいます。一括申込みによる個人を指すものではありません。</li>
          <li>（２）「当協会」とは、一般社団法人 日本コンタクトレンズ協会です。</li>
          <li>（３）「当サイト」とは、当協会が運営する継続的研修受講登録サイトを意味します。</li>
          <li>（４）「サービス」とは、当サイトにおいて当協会が提供する登録者向け各種サービスです。</li>
          <li>（５）「取引」とは、登録者が当サイトを利用して行う、継続的研修申込みや決済などの各種行為を意味します。</li>
          <li>（６）「本規約等」とは、本規約、当サイトご利用上のご注意等の総称です。</li>
          <li>（７）「登録者情報」とは、登録者が当協会に開示した登録者の属性に関する情報および登録者の取引に関する履歴等の情報（本規約第１１条により収集された情報を含みます）を意味します。</li>
        </ul>

        <h4>第２条（登録者）</h4>
        <ul>
          <li>１．登録者は、当協会が定める条件に従って、各種サービスを利用することができます。</li>
          <li>２．登録者は、当サイト内における各種サービスの利用により当協会より取得した一切の権利を譲渡、転貸、担保差入その他形態を問わず処分することはできません。</li>
        </ul>

        <h4>第３条（本規約）</h4>
        <p>本規約はすべての登録者に適用され、登録期間中に登録者に遵守していただきます。</p>

        <h4>第４条（受講するための登録手続）</h4>
        <ul>
          <li>１．受講登録者資格<br>
            本規約に同意のうえ所定の登録申込みをされた個人は、所定の登録手続完了後に登録者となり、受講者としての資格を有します。受講登録者の登録手続は、受講者となるご本人が行ってください。
          </li>
          <li>
            ２．受講者情報の入力<br>
            受講者登録手続を行うための諸注意をよく読み、所定の入力フォームに必要事項を正確に入力してください。
          </li>
        </ul>

        <h4>第５条（ユーザIDおよびパスワードの管理）</h4>
        <p>当サイトにログインするためのユーザIDおよびパスワードは、他人に知られることがないよう定期的に変更する等、登録者本人が責任をもって管理してください。入力されたユーザIDおよびパスワードが登録されたものと一致することを所定の方法により確認した場合、登録者による利用があったものとみなし、それらが盗用、不正使用その他の事情により受講者以外の者が利用している場合であっても、それにより生じた損害について当協会は一切責任を負いません。</p>

        <h4>第６条（登録情報の変更）</h4>
        <p>登録した情報に変更が生じた場合は、速やかに変更登録をお願いいたします。変更登録がなされなかったことにより生じた損害について、当協会は一切責任を負いません。また、変更登録がなされた場合でも、変更登録前にすでに手続がなされた取引は、変更登録前の情報に基づいて行われます。このような取引について登録情報の変更内容をサービス提供者に通知する必要がある場合には、登録者より受付・管理センターに直接ご連絡下さい。</p>

        <h4>第７条（登録者の登録解除）</h4>
        <p>登録者が登録解除を希望する場合には、所定の登録解除手続完了後に、登録解除となります。登録者解除手続は、登録者ご本人が行ってください。なお、当協会の管理する受講履歴に関しては削除できません。</p>

        <h4>第８条（当協会の免責）</h4>
        <ul>
          <li>１．通信回線やコンピュータなどの障害によるシステムの中断・遅滞・中止・データの消失、データへの不正アクセス等により生じた損害、その他当協会の責によらない損害について、当協会は一切責任を負わないものとします。</li>
          <li>２．当協会は、当協会のウェブページ・サーバ・ドメインなどから送られるメール・コンテンツに、コンピュータ・ウィルスなどの有害なものが含まれていないことを保証いたしません。</li>
          <li>３．当協会が登録者に適宜、情報提供やアドバイスを行ったとしても、当協会はそれにより責任を負うものではありません。</li>
          <li>４．登録者が本規約等に違反したことによって生じた損害については、当協会は一切責任を負いません。</li>
        </ul>

        <h4>第９条（禁止事項）</h4>
        <p>登録者による本サービスの利用に際しては、次の各号の行為を行うことを禁止します。</p>
        <ul>
          <li>（１）法令または本規約に違反すること</li>
          <li>（２）当協会、およびその他の第三者の権利、利益、名誉等を損ねること</li>
          <li>（３）青少年の心身に悪影響を及ぼす恐れがある行為、その他公序良俗に反する行為を行うこと</li>
          <li>（４）他の利用者その他の第三者に迷惑となる行為や不快感を抱かせる行為を行うこと</li>
          <li>（５）虚偽の情報を入力すること</li>
          <li>（６）有害なコンピュータプログラム、メール等を送信または書き込むこと</li>
          <li>（７）当協会のサーバその他のコンピュータに不正にアクセスすること</li>
          <li>（８）ユーザIDおよびパスワードを第三者に貸与・譲渡すること、または第三者と共用すること</li>
        </ul>

        <h4>第１０条（クッキー等について）</h4>
        <ul>
          <li>１．当協会は、登録者として当サイトにアクセスしたことの認証、登録者のアクセス履歴および利用状況の調査、その他登録者に最適のサービスの提供を目的に、登録者が当協会のサーバにアクセスする際のＩＰアドレスに関する情報、携帯電話端末でアクセスした場合には携帯端末の機体識別番号に関する情報、およびクッキー（cookie）の技術を使用して登録者のアクセス履歴等に関する情報を収集します。</li>
          <li>２．登録者が登録者としてサービスを利用するためには、前項を承諾し、クッキーを受け付けることが条件となります。なお、ブラウザでクッキーを拒否するための設定を行った場合、登録者としての各サービスの利用はできません。</li>
        </ul>

        <h4>第１１条（個人情報の取扱い）</h4>
        <h5>１．当協会が継続的研修の実施において取扱う個人情報</h5>
        <p>下記の個人情報を取扱います。</p>
        <ul>
          <li>(1)継続的研修にお申込みいただいた方の個人情報</li>
          <li>(2)当協会、もしくは当協会の継続的研修受付・管理センターにお問合せをいただいた方の個人情報</li>
        </ul>
        <h5>２．個人情報の利用目的</h5>
        <p>当協会は、登録者からいただいた個人情報を、以下の目的のために利用いたします。<br>
          それ以外の目的のために利用する場合は、改めて登録者に通知し、同意のご確認をいただくことといたします。</p>
        <ul>
          <li>(1)継続的研修の実施のため（お申込受付、入金確認、名簿作成、修了証作成等）。</li>
          <li>(2)継続的研修に関するお問合せ対応時の本人確認のため。</li>
          <li>(3)メール・郵便の不着等、確認事項が生じた場合、電話、電子メール等でご連絡する場合があるため。</li>
          <li>(4)支払決済の目的に個人情報を使用するため。</li>
          <li>(5)受講料の入金が確認できなかった場合、電話、電子メール等でご連絡する場合があるため。</li>
          <li>(6)修了証を研修開催後日にお渡しすることがあった場合、郵送でお届けするため。</li>
          <li>(7)次年度以降の継続的研修についてのご案内を郵送、電子メール等でお届けする場合があるため。</li>
        </ul>
        <h5>３．個人情報の第三者への提供</h5>
        <p>当協会は、法令による場合及び登録者本人の同意を得た場合以外は個人情報を第三者に提供致しません。 ただし、お申込状況・受講状況等について、個人を特定できない形で統計的に処理した情報として第三者に提供する場合があります。</p>
        <h5>４．個人情報の任意性</h5>
        <p>登録者よりご提供いただく個人情報の項目には必須項目と任意項目があります。必須項目は、研修の実施において必要な項目となります。任意項目についてはご提供いただけない項目があった場合、情報・サービス等の提供が一部限定される場合があります。</p>
        <h5>５．個人情報の取扱いの委託</h5>
        <p>当協会は、登録者本人の同意を得た登録者情報を以下の目的のために、当協会の厳正な管理下のもと、必要最小限の範囲で、外部に業務を委託することがあります。</p>
        <ul>
          <li>(1)お申込受付処理のため</li>
          <li>(2)継続的研修受付・管理センター運営のため</li>
          <li>(3)名簿・修了証作成のため</li>
          <li>(4)登録者へご案内情報等をお届けするため</li>
        </ul>
        <h5>６．個人情報の保有方法</h5>
        <p>受講記録のお問合せ等にお応えするため、登録者情報は電子データの形式で保有することとします。</p>
        <h5>７．個人情報の安全管理措置</h5>
        <p>当協会は個人情報への不正アクセス、紛失、破壊、改ざん及び漏えい等を予防するため、合理的な安全対策を講じるとともに、必要な是正措置を講じます。ただし、不正なアクセスを防止する絶対的な技術は存在しないことをご了解ください。</p>
        <h5>８．利用目的の通知、個人情報の開示、訂正、利用停止等の求めを行う方法</h5>
        <p>登録者は、当協会が保有するご自身の個人情報について開示を請求することができます。また必要な場合は訂正を求めることができます。その他、個人情報の利用停止、お問合せや苦情・相談などを申付けることが出来ます。当協会では、これらを受付けた場合、合理的な範囲で適切に対応させていただきます。</p>
        <h5>９．ログ集計について</h5>
        <p>当サイトからお申込をする場合に、登録者が利用したインターネット・サービス・プロバイダ名、ＩＰ アドレス、アクセスの日時、当サイト内でアクセスしたページ、当サイトへのアクセス元WEBサイトのURLなど、アクセスに関する特定の情報をログ集計しておりますが、ログ集計は属性を廃した匿名情報として蓄積されます。当協会は、これらの情報を、WEBサイトの運用管理やシステムの改善に役立てるために利用いたします。</p>

        <h4>第１２条（サービスの中断・停止等）</h4>
        <p>当協会は、サービスを常に良好な状態でご利用いただくために、システムの定期保守や緊急保守を行う場合、システムに負荷が集中した場合、サービスの運営に支障が生じると当協会が判断した場合、登録者のセキュリティを確保する必要が生じた場合、その他必要があると判断した場合には、事前に通知することなく、サービスの全部または一部の提供を中断または停止する等の必要な措置を取ることができるものとします。この場合に登録者に生じた損害について、当協会は一切責任を負わないものとします。</p>

        <h4>第１３条（特定登録者の利用停止・登録者資格取消）</h4>
        <p>１．当協会は、特定の登録者が次の各号に該当すると判断した場合には、事前に通知することなく当該登録者によるサービスの利用停止、当該登録者のユーザIDおよびパスワードの変更、または当該登録者の登録者資格の取消しを行うことができるものとします。これにより登録者に何らかの損害が生じたとしても、当協会は一切責任を負わないものとします。</p>
        <ul>
          <li>（１）登録者に法令や本規約等に違反する行為があった場合</li>
          <li>（２）登録者にサービス利用に関して不正行為があった場合</li>
          <li>（３）一定回数以上のパスワードの入力ミスがあるなど登録者のセキュリティを確保するために必要な場合</li>
          <li>（４）その他当協会が不適切と判断した場合</li>
        </ul>
        <p>２．前項のほか、登録者が当協会の定めた期間に一度もログインを行わなかった場合は、当協会は、事前に通知することなく当該登録者のユーザIDおよびパスワードの変更または登録者資格の取消しを行うことができるものとします。</p>

        <h4>第１４条（サービスの変更・廃止）</h4>
        <p>当協会は、サービスの全部または一部を適宜変更・廃止できるものとします。</p>

        <h4>第１５条（本規約の改定）</h4>
        <p>当協会は、本規約を任意に改定できるものとし、また、当協会において本規約を補充する規約（以下「補充規約」といいます）を定めることができます。</p>

        <h4>第１６条（準拠法、合意管轄）</h4>
        <p>本規約は日本法に基づき解釈されるものとし、本規約に関し訴訟の必要が生じた場合には、東京地方裁判所を第一審の専属的合意管轄裁判所といたします。</p>

        <p class="register_main--end">以上</p>
        <p class="register_main--end">2014年2月21日制定</p>

        <div class="btnContainer">
          <button
            class="c-button c-button--medium c-button--yellow"
            @click="confirmAgreement"
          >
            同意する
          </button>
          <a
            class="c-button c-button--medium c-button--border"
            @click="$router.go(-1)"
          >
            戻る
          </a>
        </div>
      </div>

      <form
        v-else
        @submit.prevent="sendAccountForm"
      >
        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>メールアドレス</label>
          <input
            v-model="models.email.value"
            type="text"
            class="c-input-text"
            placeholder="sample@example.com"
            autocomplete="off"
            maxlength="100"
          >
          <span
            v-if="errors.email"
            class="c-validationError"
          >{{ errors.email }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>ログインID</label>
          <input
            v-model="models.identifier.value"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="16"
          >
          <span
            v-if="errors.identifier"
            class="c-validationError"
          >{{ errors.identifier }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>パスワード</label>
          <input
            v-model="models.password.value"
            :type="isShownPassword ? 'text' : 'password'"
            class="c-input-password"
            placeholder=""
            autocomplete="off"
            maxlength="50"
          >
          <div class="c-password-btn" @click="togglePasswordSeek">
            <img :src="isShownPassword ? require('@assets/images/icon/icon-hidden.svg') : require('@assets/images/icon/icon-display.svg')">
          </div>
          <span
            v-if="errors.password"
            class="c-validationError"
          >{{ errors.password }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>パスワード(確認)</label>
          <input
            v-model="models.passwordConfirm.value"
            :type="isShownConfirmPassword ? 'text' : 'password'"
            class="c-input-password"
            placeholder=""
            autocomplete="off"
            maxlength="50"
          >
          <div class="c-password-btn" @click="toggleConfirmPasswordSeek">
            <img :src="isShownConfirmPassword ? require('@assets/images/icon/icon-hidden.svg') : require('@assets/images/icon/icon-display.svg')">
          </div>
          <span
            v-if="errors.passwordConfirm"
            class="c-validationError"
          >{{ errors.passwordConfirm }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>氏名</label>
          <input
            id="password"
            v-model="models.lastName.value"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="山田"
            autocomplete="off"
            maxlength="256"
          ><input
            id="password"
            v-model="models.firstName.value"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="太郎"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.lastName"
            class="c-validationError"
          >{{ errors.lastName }}</span>
          <span
            v-if="errors.firstName"
            class="c-validationError"
          >{{ errors.firstName }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>フリガナ</label>
          <input
            id="password"
            v-model="models.lastNameKatakana.value"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="ヤマダ"
            autocomplete="off"
            maxlength="256"
          ><input
            id="password"
            v-model="models.firstNameKatakana.value"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="タロウ"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.lastNameKatakana"
            class="c-validationError"
          >{{ errors.lastNameKatakana }}</span>
          <span
            v-if="errors.firstNameKatakana"
            class="c-validationError"
          >{{ errors.firstNameKatakana }}</span>
        </div>

        <!-- <div class="p-form-field">
          <label
            class="c-input-label"
          >ニックネーム</label>
          <input
            v-model="models.nickname.value"
            type="text"
            class="c-input-text"
            placeholder="ヤマダタロウ"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.nickname"
            class="c-validationError"
          >{{ errors.nickname }}</span>
        </div> -->

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>生年月日</label>
          <div class="c-input-birthday-box">
            <select
              v-model="models.year.value"
              class="c-input-select"
            >
              <option
                v-for="(item, index) in optsYear"
                :key="index"
                :selected="index === 0"
                :value="item.value"
              >
                {{ item.value }}
              </option>
            </select>
            <span>年</span>

            <select
              v-model="models.month.value"
              class="c-input-select"
            >
              <option
                v-for="(item, index) in months"
                :key="index"
                :selected="index === 0"
                :value="item"
              >
                {{ item }}
              </option>
            </select>
            <span>月</span>

            <select
              v-model="models.date.value"
              class="c-input-select"
            >
              <option
                v-for="(item, index) in days"
                :key="index"
                :selected="index === 0"
              >
                {{ item }}
              </option>
            </select>
            <span>日</span>
          </div>
          <span
            v-if="errors.year"
            class="c-validationError"
          >{{ errors.year }}</span>
          <span
            v-if="errors.month"
            class="c-validationError"
          >{{ errors.month }}</span>
          <span
            v-if="errors.date"
            class="c-validationError"
          >{{ errors.date }}</span>
          <span
            v-if="birthdayError"
            class="c-validationError"
          >{{ birthdayError }}</span>
        </div>

        <div class="p-form-field p-form-field--required">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>性別</label>
          <div class="p-filter-items__list">
            <div
              v-for="item, index in GENDER_OPTIONS"
              :key="index"
              class="c-checkbox-switch p-filter-items__item"
            >
              <label class="c-checkbox-switch__label">
                <input
                  v-model="models.gender.value"
                  type="radio"
                  name="gender"
                  :value="item.value"
                >
                <span>{{ item.name }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>自宅都道府県</label>
          <select
            v-model="models.pref.value"
            class="c-input-select"
          >
            <option value="">
              未選択
            </option>
            <option
              v-for="(item, index) in PREF_OPTIONS"
              :key="index"
              :value="item.value"
              :selected="item.value === models.pref.value"
            >
              {{ item.name }}
            </option>
          </select>
          <span
            v-if="errors.pref"
            class="c-validationError"
          >{{ errors.pref }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>営業所会社名</label>
          <input
            v-model="models.office.value"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="100"
          >
          <span
            v-if="errors.office"
            class="c-validationError"
          >{{ errors.office }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          >営業所屋号（店舗名）</label>
          <input
            v-model="models.officeSection.value"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="100"
          >
          <span
            v-if="errors.officeSection"
            class="c-validationError"
          >{{ errors.officeSection }}</span>
        </div>

        <div class="p-form-field zipcode">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>会社住所</label>
          <input
            v-model="models.officeZipcode.value"
            type="text"
            class="c-input-text"
            placeholder="郵便番号"
            autocomplete="off"
            maxlength="7"
          >

          <button
            class="zipCodeBtn"
            @click.prevent="searchAddress()"
          >
            検索
          </button>

          <select
            v-model="models.officePref.value"
            class="c-input-select"
          >
            <option value="">
              都道府県
            </option>
            <option
              v-for="(item, index) in PREF_OPTIONS"
              :key="index"
              :value="item.value"
              :selected="item.value === models.pref.value"
            >
              {{ item.name }}
            </option>
          </select>
          <span
            v-if="errors.officeZipcode"
            class="c-validationError"
          >{{ errors.officeZipcode }}</span>
          <span
            v-if="errors.officePref"
            class="c-validationError"
          >{{ errors.officePref }}</span>
        </div>

        <div class="p-form-field">
          <input
            v-model="models.officeCity.value"
            type="text"
            class="c-input-text"
            placeholder="市区町村"
            autocomplete="off"
            maxlength="128"
          >
          <span
            v-if="errors.officeCity"
            class="c-validationError"
          >{{ errors.officeCity }}</span>
        </div>

        <div class="p-form-field">
          <input
            v-model="models.officeStreet.value"
            type="text"
            class="c-input-text"
            placeholder="番地"
            autocomplete="off"
            maxlength="128"
          >
          <span
            v-if="errors.officeStreet"
            class="c-validationError"
          >{{ errors.officeStreet }}</span>
        </div>

        <div class="p-form-field">
          <input
            v-model="models.officeBuilding.value"
            type="text"
            class="c-input-text"
            placeholder="建物名（ビル名）"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.officeBuilding"
            class="c-validationError"
          >{{ errors.officeBuilding }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>会社電話番号</label>
          <input
            v-model="models.officeTel.value"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="20"
          >
          <span
            v-if="errors.officeTel"
            class="c-validationError"
          >{{ errors.officeTel }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          >会社FAX番号</label>
          <input
            v-model="models.officeFax.value"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="20"
          >
          <span
            v-if="errors.officeFax"
            class="c-validationError"
          >{{ errors.officeFax }}</span>
        </div>

        <div class="p-form-field checkbox">
          <label
            class="c-input-label"
          >販売責任者</label>
          <div class="p-filter-items__list">
            <div class="c-checkbox-switch p-filter-items__item">
              <label class="c-checkbox-switch__label">
                <input
                  v-model="models.officeManagerFlag.value"
                  type="radio"
                  name="officeManagerFlag"
                  :value="true"
                >
                <span>はい</span>
              </label>
            </div>
            <div class="c-checkbox-switch p-filter-items__item">
              <label class="c-checkbox-switch__label">
                <input
                  v-model="models.officeManagerFlag.value"
                  type="radio"
                  name="officeManagerFlag"
                  :value="false"
                >
                <span>いいえ</span>
              </label>
            </div>
          </div>
        </div>

        <div class="btnContainer">
          <button
            class="c-button c-button--medium c-button--yellow"
            :disabled="moveConfirmButtonDisabled || birthdayError !== ''"
            @click="moveConfirm"
          >
            確認
          </button>
          <RouterLink
            :to="{name:'Login'}"
            class="c-button c-button--medium c-button--border"
          >
            戻る
          </RouterLink>
        </div>
      </form>
    </div>
  </section>
</template>

<style scoped lang="scss">
/* 利用規約本文 */
.register_main {
  width: 100%;
  margin: 0 auto 0;
  font-size: 14px;

  h2 {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 20px;
  }
  h3 {
    font-size: 19.2px;
    font-weight: bold;
    margin: 20px 0 10px;
  }
  h4 {
    font-size: 16px;
    font-weight: bold;
    margin: 15px 0 5px;
  }
  h5 {
    font-size: 14.4px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  p {
    margin-bottom: 10px;
  }

  ul {
    padding: 0;
    li {
      padding-left: 15px;
      text-indent: -15px;
      list-style: none;
    }
  }

  &--end {
    text-align: right;
  }
}

/* 入力フォーム */
.container {
  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }
}
.c-input-select {
  width: 100px;
  height: 32px;
}
.zipcode {
  .c-input-text {
    width: 80px;
  }
  .zipCodeBtn {
    display: inline-block;
    width: 50px;
    height: 30px;
    margin: 0 15px 0 15px;
    border-radius: 4px;
    text-align: center;
    background: var(--corporate);
    color: #000;
    vertical-align: 6px;
  }
}
.p-form-field.checkbox {
  label {
    margin-right: 15px;
  }
  input[type="checkbox"] {
    appearance: auto;
    vertical-align: -1px;
  }
}

.btnContainer {
  margin-top: 20px;
}
</style>
